import React, { useMemo, useState } from 'react';
import { 
  Book, 
  Code, 
  Play, 
  Copy, 
  CheckCircle, 
  Zap,
  Users,
  Video,
  Key,
  Shield,
  Settings
} from 'lucide-react';

interface CodeExample {
  title: string;
  language: string;
  code: string;
}

const codeExamples: CodeExample[] = [
  {
    title: 'Test API Connection',
    language: 'javascript',
    code: `// Test your API connection
const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/health', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json'
  }
});

const health = await response.json();
console.log('API Status:', health.status);
// Output: { status: "healthy", timestamp: "2025-08-02T16:03:18.884Z", version: "v1" }`
  },
  {
    title: 'Create a Video Room',
    language: 'javascript',
    code: `// Create a new video conference room
const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/rooms', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Team Meeting',
    maxParticipants: 10,
    settings: {
      enableVideo: true,
      enableAudio: true,
      enableScreenShare: true
    }
  })
});

const room = await response.json();
console.log('Room created:', room.id);`
  },
  {
    title: 'Create API Key (Admin)',
    language: 'javascript',
    code: `// Create an API key for your organization
const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/admin/api-keys', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    orgId: 'org_demo_123456789',
    name: 'My API Key',
    permissions: ['video:create', 'video:join'],
    usageLimit: 1000
  })
});

const apiKey = await response.json();
console.log('API Key created:', apiKey.key);
// Save this key securely - you won't see it again!`
  },
  {
    title: 'Generate Join Token',
    language: 'javascript',
    code: `// Generate a token for a participant to join
const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/tokens', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    roomId: 'room_abc123',
    participantName: 'John Doe',
    permissions: ['video', 'audio', 'screenshare']
  })
});

const { token } = await response.json();
console.log('Join token:', token);`
  },
  {
    title: 'Join Room (Frontend)',
    language: 'javascript',
    code: `// Join a room from the frontend
import { CustomerConnectsSDK } from '@customerconnects/sdk';

const sdk = new CustomerConnectsSDK({
  token: 'participant_token_here'
});

// Join the room
await sdk.join({
  video: true,
  audio: true,
  onParticipantJoined: (participant) => {
    console.log('Participant joined:', participant.name);
  },
  onParticipantLeft: (participant) => {
    console.log('Participant left:', participant.name);
  }
});`
  },
  {
    title: 'Webhook Handler',
    language: 'javascript',
    code: `// Handle webhook events
app.post('/webhooks/customerconnects', (req, res) => {
  const { event, data } = req.body;
  
  switch (event) {
    case 'room.created':
      console.log('Room created:', data.roomId);
      break;
    case 'participant.joined':
      console.log('Participant joined:', data.participantId);
      break;
    case 'participant.left':
      console.log('Participant left:', data.participantId);
      break;
    case 'room.ended':
      console.log('Room ended:', data.roomId);
      break;
  }
  
  res.status(200).send('OK');
});`
  }
];

export const DocumentationPage: React.FC = () => {
  const [activeExample, setActiveExample] = useState(0);
  const [copiedCode, setCopiedCode] = useState<number | null>(null);

  // Live tester state
  const [selectedEndpoint, setSelectedEndpoint] = useState<'health' | 'apiKeys' | 'analytics'>('health');
  const [liveResponse, setLiveResponse] = useState<string>('');
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);

  const docsBaseForCurl = useMemo(() => (
    process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8787' : 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev'
  ), []);
  const docsBaseForFetch = useMemo(() => (
    process.env.NODE_ENV === 'development' ? '' : 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev'
  ), []);

  const copyCode = async (code: string, index: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(index);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const buildCurlAndPath = () => {
    // Tryable endpoints that don't require API keys
    const orgIdParam = 'org_demo_123456789';
    switch (selectedEndpoint) {
      case 'apiKeys': {
        const path = `/admin/api-keys?orgId=${orgIdParam}`;
        const curl = `curl -s '${docsBaseForCurl}${path}'`;
        return { path, curl, method: 'GET' };
      }
      case 'analytics': {
        const path = `/admin/analytics?orgId=${orgIdParam}&days=7`;
        const curl = `curl -s '${docsBaseForCurl}${path}'`;
        return { path, curl, method: 'GET' };
      }
      case 'health':
      default: {
        const path = `/health`;
        const curl = `curl -s '${docsBaseForCurl}${path}'`;
        return { path, curl, method: 'GET' };
      }
    }
  };

  const runLiveRequest = async () => {
    setLiveError(null);
    setLiveLoading(true);
    setLiveResponse('');
    const { path, method } = buildCurlAndPath();
    try {
      const res = await fetch(`${docsBaseForFetch}${path}`, { method });
      const text = await res.text();
      setLiveResponse(text);
    } catch (e) {
      setLiveError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLiveLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">
          API Documentation
        </h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          Everything you need to integrate video conferencing into your application with Customer Connects API
        </p>
      </div>

      {/* Quick Start */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-8">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center mr-4">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-primary-900 dark:text-primary-100">Quick Start</h2>
            <p className="text-primary-700 dark:text-primary-300">Get up and running in minutes</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">1</span>
            </div>
            <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">Get API Key</h3>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              Create an account and generate your API key from the dashboard
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">2</span>
            </div>
            <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">Create Room</h3>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              Make your first API call to create a video conference room
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">3</span>
            </div>
            <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">Start Building</h3>
            <p className="text-sm text-primary-700 dark:text-primary-300">
              Integrate video conferencing into your application
            </p>
          </div>
        </div>
      </div>

      {/* Core Concepts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center mb-4">
            <Video className="w-6 h-6 text-info mr-3" />
            <h3 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Rooms</h3>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
            Rooms are virtual spaces where participants can join for video conferences. Each room has a unique ID and configurable settings.
          </p>
          <ul className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <li>• Maximum 50 participants per room</li>
            <li>• HD video and crystal clear audio</li>
            <li>• Screen sharing capabilities</li>
            <li>• Real-time chat messaging</li>
          </ul>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center mb-4">
            <Key className="w-6 h-6 text-warning mr-3" />
            <h3 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Tokens</h3>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
            Secure tokens allow participants to join rooms. Tokens contain participant information and permissions.
          </p>
          <ul className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <li>• Time-limited for security</li>
            <li>• Customizable permissions</li>
            <li>• Participant metadata support</li>
            <li>• Role-based access control</li>
          </ul>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-success mr-3" />
            <h3 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Webhooks</h3>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
            Receive real-time notifications about room events, participant activities, and system updates.
          </p>
          <ul className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <li>• Real-time event notifications</li>
            <li>• Automatic retry on failure</li>
            <li>• Event filtering options</li>
            <li>• Secure payload verification</li>
          </ul>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-6 h-6 text-error mr-3" />
            <h3 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Analytics</h3>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
            Track usage metrics, monitor performance, and gain insights into your video conferencing usage patterns.
          </p>
          <ul className="space-y-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <li>• Real-time usage metrics</li>
            <li>• Historical data analysis</li>
            <li>• Custom reporting</li>
            <li>• Performance monitoring</li>
          </ul>
        </div>
      </div>

      {/* Code Examples */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Code className="w-6 h-6 text-primary-500 mr-3" />
              <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
                Code Examples
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {codeExamples.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveExample(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    activeExample === index 
                      ? 'bg-primary-500' 
                      : 'bg-light-bg-quaternary dark:bg-dark-bg-quaternary'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {codeExamples.map((example, index) => (
              <button
                key={index}
                onClick={() => setActiveExample(index)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  activeExample === index
                    ? 'bg-primary-500 text-white'
                    : 'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                }`}
              >
                {example.title}
              </button>
            ))}
          </div>

          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
                {codeExamples[activeExample].title}
              </h3>
              <button
                onClick={() => copyCode(codeExamples[activeExample].code, activeExample)}
                className="flex items-center px-3 py-1 text-sm bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary rounded transition-colors"
              >
                {copiedCode === activeExample ? (
                  <>
                    <CheckCircle size={16} className="mr-1 text-success" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
            
            <pre className="bg-dark-bg-primary text-dark-text-primary p-4 rounded-lg text-sm overflow-x-auto">
              <code>{codeExamples[activeExample].code}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* API Reference */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center">
            <Book className="w-6 h-6 text-info mr-3" />
            <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
              API Reference
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-200">
              <strong>Base URL:</strong> {process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8787' : 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">Core API Endpoints</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-mono text-sm bg-success/10 text-success px-2 py-1 rounded mr-2">POST</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/api/v1/rooms</span>
                  </div>
                  <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Create room</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-mono text-sm bg-info/10 text-info px-2 py-1 rounded mr-2">GET</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/api/v1/rooms/&#123;id&#125;</span>
                  </div>
                  <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Get room</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-mono text-sm bg-success/10 text-success px-2 py-1 rounded mr-2">POST</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/api/v1/tokens</span>
                  </div>
                  <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Generate token</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-mono text-sm bg-error/10 text-error px-2 py-1 rounded mr-2">DEL</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/api/v1/rooms/&#123;id&#125;</span>
                  </div>
                  <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Delete room</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">Admin/Portal Endpoints</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div>
                    <span className="font-mono text-sm bg-success/10 text-success px-2 py-1 rounded mr-2">POST</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/admin/api-keys</span>
                  </div>
                  <span className="text-xs text-green-600 dark:text-green-400">✓ Working</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div>
                    <span className="font-mono text-sm bg-info/10 text-info px-2 py-1 rounded mr-2">GET</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/admin/api-keys</span>
                  </div>
                  <span className="text-xs text-green-600 dark:text-green-400">✓ Working</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div>
                    <span className="font-mono text-sm bg-info/10 text-info px-2 py-1 rounded mr-2">GET</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/health</span>
                  </div>
                  <span className="text-xs text-green-600 dark:text-green-400">✓ Working</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div>
                    <span className="font-mono text-sm bg-info/10 text-info px-2 py-1 rounded mr-2">GET</span>
                    <span className="text-light-text-primary dark:text-dark-text-primary">/api/v1/analytics</span>
                  </div>
                  <span className="text-xs text-yellow-600 dark:text-yellow-400">⚠ In Progress</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live Endpoint Tester */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="p-6 border-b border-light-border dark:border-dark-border flex items-center justify-between">
          <div className="flex items-center">
            <Play className="w-6 h-6 text-success mr-3" />
            <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">Live Endpoint Tester</h2>
          </div>
          <select
            className="px-3 py-2 rounded border bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-primary dark:text-dark-text-primary"
            value={selectedEndpoint}
            onChange={(e) => setSelectedEndpoint(e.target.value as any)}
          >
            <option value="health">GET /health</option>
            <option value="apiKeys">GET /admin/api-keys?orgId=…</option>
            <option value="analytics">GET /admin/analytics?orgId=…&days=7</option>
          </select>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">Sample curl</h3>
              <button
                onClick={() => copyCode(buildCurlAndPath().curl, 999)}
                className="flex items-center px-2 py-1 text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary rounded"
              >
                <Copy size={14} className="mr-1" /> Copy
              </button>
            </div>
            <pre className="bg-dark-bg-primary text-dark-text-primary p-3 rounded text-xs overflow-x-auto">
              <code>{buildCurlAndPath().curl}</code>
            </pre>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={runLiveRequest}
              className="px-4 py-2 bg-success text-white rounded hover:opacity-90"
              disabled={liveLoading}
            >
              {liveLoading ? 'Running…' : 'Run'}
            </button>
            {liveError && <span className="text-error text-sm">{liveError}</span>}
          </div>

          <div>
            <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Response</h3>
            <pre className="bg-dark-bg-primary text-dark-text-primary p-3 rounded text-xs overflow-x-auto min-h-[100px]">
              <code>{liveResponse || '// Run to see live response here'}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* Getting Help */}
      <div className="bg-gradient-to-r from-primary-50 to-info-50 dark:from-primary-900/20 dark:to-info-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-8">
        <div className="text-center">
          <Users className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-primary-900 dark:text-primary-100 mb-2">
            Need Help?
          </h2>
          <p className="text-primary-700 dark:text-primary-300 mb-6 max-w-2xl mx-auto">
            Our team is here to help you succeed. Whether you need technical support, have questions about integration, or want to discuss your use case.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
              <Book className="w-5 h-5 mr-2" />
              View Full Docs
            </button>
            <button className="flex items-center px-6 py-3 bg-white dark:bg-dark-bg-primary text-primary-500 border border-primary-200 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors">
              <Users className="w-5 h-5 mr-2" />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};